import getEnv, { Environment } from './env';

export type Product = 'chart' | 'chartblog' | 'co' | 'press' | 'spark';

interface Config {
  GTM_ID?: string | undefined;
  GTM_AUTH?: string | undefined;
  GTM_PREVIEW?: string | undefined;
};

const GTM_ID = (product: Product): string | undefined => ({
  chart: 'GTM-NN6WN6',
  chartblog: 'GTM-WHFPSDX',
  co: 'GTM-KWRJ7Q2',
  press: 'GTM-5NTB7NT',
  spark: 'GTM-W5SZPS2'
}[product]);

const GTM_CONFIG = (product: Product, currentEnv?: Environment) => ({
  chart: {
    production: {
      // https://lucidchart.com
      GTM_ID: GTM_ID(product),
      GTM_AUTH: undefined,
      GTM_PREVIEW: undefined
    },
    preprod: {
      // https://preprodchart.com
      GTM_ID: GTM_ID(product),
      GTM_AUTH: 'LU4apM-y_B6d0rzc5U6MBA',
      GTM_PREVIEW: 'env-381'
    },
    staging: {
      // https://*.lucidchart.ninja/pages
      GTM_ID: GTM_ID(product),
      GTM_AUTH: 'HsnZz2hwA8BIvD876dUn8Q',
      GTM_PREVIEW: 'env-382'
    },
    development: {
      // http(s)://localhost:8000
      GTM_ID: GTM_ID(product),
      GTM_AUTH: 'RYuw9WeIAPh8TeSN-Zy-8A',
      GTM_PREVIEW: 'env-380'
    },
    test: {
      // http(s)://localhost
      GTM_ID: 'testGTM_ID',
      GTM_AUTH: 'testGTM_AUTH',
      GTM_PREVIEW: 'testGTM_PREVIEW'
    }
  },
  chartblog: {
    production: {
      // https://lucidchart.com/blog
      GTM_ID: GTM_ID(product),
      GTM_AUTH: undefined,
      GTM_PREVIEW: undefined
    },
    preprod: {
      // https://preprodchart.com/blog
      GTM_ID: GTM_ID(product),
      GTM_AUTH: undefined,
      GTM_PREVIEW: undefined
    },
    staging: {
      // https://*.lucidchart.ninja/blog
      GTM_ID: GTM_ID(product),
      GTM_AUTH: undefined,
      GTM_PREVIEW: undefined
    },
    development: {
      // http(s)://localhost/lucidchart-blog
      GTM_ID: GTM_ID(product),
      GTM_AUTH: undefined,
      GTM_PREVIEW: undefined
    },
    test: {
      // http(s)://localhost
      GTM_ID: 'testGTM_ID',
      GTM_AUTH: 'testGTM_AUTH',
      GTM_PREVIEW: 'testGTM_PREVIEW'
    }
  },
  co: {
    production: {
      // https://lucid.co
      GTM_ID: GTM_ID(product),
      GTM_AUTH: undefined,
      GTM_PREVIEW: undefined
    },
    preprod: {
      // https://preprodgo.com
      GTM_ID: GTM_ID(product),
      GTM_AUTH: 'LU4apM-y_B6d0rzc5U6MBA',
      GTM_PREVIEW: 'env-381'
    },
    staging: {
      // https://*.golucid.ninja
      GTM_ID: GTM_ID(product),
      GTM_AUTH: 'FsHSmrw2AD-n3sHWPPIEeQ',
      GTM_PREVIEW: 'env-6'
    },
    development: {
      // http(s)://localhost:8000
      GTM_ID: GTM_ID(product),
      GTM_AUTH: 'FdxCAsI5zFkVAq6lK4ZYwg',
      GTM_PREVIEW: 'env-7'
    },
    test: {
      // http(s)://localhost
      GTM_ID: 'testGTM_ID',
      GTM_AUTH: 'testGTM_AUTH',
      GTM_PREVIEW: 'testGTM_PREVIEW'
    }
  },
  press: {
    production: {
      // https://lucidpress.com
      GTM_ID: GTM_ID(product),
      GTM_AUTH: undefined,
      GTM_PREVIEW: undefined
    },
    preprod: {
      // https://preprodpress.com
      GTM_ID: GTM_ID(product),
      GTM_AUTH: undefined,
      GTM_PREVIEW: undefined
    },
    staging: {
      // https://*.lucidpress.ninja
      GTM_ID: GTM_ID(product),
      GTM_AUTH: undefined,
      GTM_PREVIEW: undefined
    },
    development: {
      // http(s)://localhost:8000
      GTM_ID: GTM_ID(product),
      GTM_AUTH: undefined,
      GTM_PREVIEW: undefined
    },
    test: {
      // http(s)://localhost
      GTM_ID: 'testGTM_ID',
      GTM_AUTH: 'testGTM_AUTH',
      GTM_PREVIEW: 'testGTM_PREVIEW'
    }
  },
  spark: {
    production: {
      // https://lucidspark.com
      GTM_ID: GTM_ID(product),
      GTM_AUTH: undefined,
      GTM_PREVIEW: undefined
    },
    preprod: {
      // https://preprodgo.com
      GTM_ID: GTM_ID(product),
      GTM_AUTH: undefined,
      GTM_PREVIEW: undefined
    },
    staging: {
      // https://*.staginspark.lucidmarketingtest.com
      GTM_ID: GTM_ID(product),
      GTM_AUTH: undefined,
      GTM_PREVIEW: undefined
    },
    development: {
      // http(s)://localhost:8000
      GTM_ID: GTM_ID(product),
      GTM_AUTH: undefined,
      GTM_PREVIEW: undefined
    },
    test: {
      // http(s)://localhost
      GTM_ID: 'testGTM_ID',
      GTM_AUTH: 'testGTM_AUTH',
      GTM_PREVIEW: 'testGTM_PREVIEW'
    }
  }
}[product][currentEnv || 'development']);

const gtmConfig = (product: Product): Config => {
  const currentEnv = getEnv() as Environment;

  const id = GTM_CONFIG(product, currentEnv).GTM_ID;
  const auth = GTM_CONFIG(product, currentEnv).GTM_AUTH;
  const preview = GTM_CONFIG(product, currentEnv).GTM_PREVIEW;

  return { GTM_ID: id, GTM_AUTH: auth, GTM_PREVIEW: preview };
};

export default gtmConfig;
